import { usePhonePlans } from '@/hooks/usePhonePlans';
import { useOperators } from '@/hooks/useOperator';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { cn } from '@/lib/utils';
import type { PhonePlan } from '@octave/contracts';
import { Phone, Wifi } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PlanDetailsDialog } from './PlanDetailsDialog';
import { formatPrice } from '@/lib/format';

const operatorImages: Record<string, string> = {
  Orange: '/images/operators/orange.webp',
  SFR: '/images/operators/sfr.svg',
  Bouygues: '/images/operators/bouygues.png',
};


export function ForfaitsTab() {
  const { data: organization } = useCurrentOrganization();
  const { data: operators, isLoading: operatorsLoading } = useOperators();

  const [operatorId, setOperatorId] = useState<string>('');
  const [mobileLineType, setMobileLineType] = useState<'classic' | 'dataonly'>(
    'classic'
  );
  const [selectedPlan, setSelectedPlan] = useState<PhonePlan | null>(null);

  const selectedOperator = useMemo(
    () => operators?.find((o) => o.id === operatorId),
    [operators, operatorId]
  );

  const availableTypes = useMemo(
    () =>
      selectedOperator?.availableTypes?.filter(
        (t) => t === 'classic' || t === 'dataonly'
      ) || [],
    [selectedOperator]
  );

  useEffect(() => {
    if (!operatorId && operators && operators.length > 0) {
      setOperatorId(operators[0].id);
    }
  }, [operators, operatorId]);

  useEffect(() => {
    if (
      availableTypes.length > 0 &&
      !availableTypes.includes(mobileLineType)
    ) {
      setMobileLineType('classic');
    }
  }, [availableTypes, mobileLineType]);

  const { data: plans, isLoading: plansLoading } = usePhonePlans(
    organization?.id,
    operatorId,
    mobileLineType
  );

  if (operatorsLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Selection card */}
      <div className="inline-block rounded-2xl bg-white shadow-sm p-6 space-y-6">
        {/* Operator selection */}
        <section>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Opérateur
          </label>
          <div className="flex flex-wrap gap-3">
            {operators?.map((op) => (
              <button
                key={op.id}
                type="button"
                onClick={() => setOperatorId(op.id)}
                className={cn(
                  'flex flex-col items-center gap-2 rounded-xl border p-4 w-36 cursor-pointer transition-all',
                  operatorId === op.id
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
                )}
              >
                <img
                  src={
                    operatorImages[op.name] || '/images/operators/default.svg'
                  }
                  alt={op.name}
                  className="w-12 h-12 object-contain"
                />
                <span className="text-sm font-medium text-slate-900">
                  {op.name}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Line type toggle */}
        {operatorId && (
          <section>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Type de ligne
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setMobileLineType('classic')}
                className={cn(
                  'flex items-center gap-3 rounded-xl border px-4 py-3 flex-1 sm:flex-none sm:w-64 cursor-pointer transition-all',
                  mobileLineType === 'classic'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
                )}
              >
                <Phone className="h-4 w-4 text-slate-500 shrink-0" />
                <div className="text-left">
                  <p className="text-sm font-medium text-slate-900">
                    Ligne mobile
                  </p>
                  <p className="text-xs text-slate-500">Appels, SMS, Data</p>
                </div>
              </button>
              {availableTypes.includes('dataonly') && (
                <button
                  type="button"
                  onClick={() => setMobileLineType('dataonly')}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border px-4 py-3 flex-1 sm:flex-none sm:w-64 cursor-pointer transition-all',
                    mobileLineType === 'dataonly'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
                  )}
                >
                  <Wifi className="h-4 w-4 text-slate-500 shrink-0" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-900">
                      SIM Data
                    </p>
                    <p className="text-xs text-slate-500">Internet uniquement</p>
                  </div>
                </button>
              )}
            </div>
          </section>
        )}
      </div>

      {/* Plans grid */}
      <section>
        {plansLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : plans && plans.length > 0 ? (
          <>
            <p className="text-xs text-slate-400 mb-4">
              {plans.length} forfait{plans.length > 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setSelectedPlan(plan)}
                  className="group text-left rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-transparent hover:border-emerald-100 p-5 space-y-4 cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                        plan.isDataOnly
                          ? 'bg-sky-50 text-sky-700'
                          : 'bg-emerald-50 text-emerald-700'
                      )}
                    >
                      {plan.isDataOnly ? (
                        <Wifi className="h-3 w-3" />
                      ) : (
                        <Phone className="h-3 w-3" />
                      )}
                      {plan.isDataOnly ? 'Data' : 'Mobile'}
                    </span>
                    <img
                      src={
                        operatorImages[plan.operator.name] ||
                        '/images/operators/default.svg'
                      }
                      alt={plan.operator.name}
                      className="h-6 w-6 object-contain"
                    />
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {plan.name}
                    </p>
                  </div>

                  <div className="flex items-end justify-between pt-3 border-t border-slate-100">
                    <div>
                      <p className="text-xs text-slate-400">Prix mensuel</p>
                      <p className="text-lg font-bold text-slate-900">
                        {formatPrice(plan.price)}
                        <span className="text-xs font-normal text-slate-500">
                          {' '}
                          /mois
                        </span>
                      </p>
                    </div>
                    <span className="text-xs font-medium text-emerald-600 group-hover:underline">
                      Commander →
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-sm text-slate-500">
              Aucun forfait disponible pour cet opérateur.
            </p>
          </div>
        )}
      </section>

      <PlanDetailsDialog
        plan={selectedPlan}
        onClose={() => setSelectedPlan(null)}
      />
    </div>
  );
}
